import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, MessageCircle, Wrench } from "lucide-react";
import { CONTACTS, IMAGES, scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 spotlight" aria-hidden="true" />
      <div className="absolute top-24 right-0 font-mono text-[10px] uppercase tracking-[0.3em] text-ash rotate-90 origin-right hidden xl:block" aria-hidden="true">
        Empoli · Toscana · dal 2006
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.22em] text-copper mb-7 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-copper inline-block" />
            Tecnico informatico · Empoli e dintorni · oltre 20 anni di esperienza
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-[4.2rem] text-ink">
            <Line delay={0.25}>Ridò vita ai tuoi</Line>
            <Line delay={0.37}>
              computer<span className="text-copper">.</span> Progetto
            </Line>
            <Line delay={0.49}>
              reti <em className="not-italic text-outline">d'eccellenza</em><span className="text-copper">.</span>
            </Line>
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 text-base sm:text-lg leading-relaxed text-smoke max-w-xl"
          >
            Assistenza hardware e software, server, smartphone e siti internet.
            Soluzioni dirette, trasparenti e senza complicazioni: parli con il tecnico, non con un call center.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-contact-cta-button"
              onClick={() => scrollToId("contatti")}
              className="group flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-copper text-white font-display font-semibold text-sm tracking-wide hover:bg-copper-dark transition-colors duration-300"
            >
              Richiedi un intervento
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-whatsapp-cta-button"
              href={CONTACTS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-7 py-3.5 rounded-full border border-ink/20 font-display font-semibold text-sm text-ink hover:border-copper hover:text-copper transition-colors duration-300"
            >
              <MessageCircle size={16} /> Chatta su WhatsApp
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              ["20+", "anni di attività"],
              ["6", "aree di intervento"],
              ["1", "tecnico, zero call center"],
            ].map(([n, l]) => (
              <div key={l} className="flex items-baseline gap-2.5">
                <span className="font-display font-extrabold text-3xl text-ink">{n}</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ash">{l}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5" onMouseMove={onMouse} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
            style={{ rotateX, rotateY, transformPerspective: 900 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-copper/40 rounded-[1.6rem] translate-x-4 translate-y-4" aria-hidden="true" />
            <div className="relative rounded-[1.4rem] overflow-hidden bg-night aspect-[4/5] shadow-[0_30px_80px_rgba(18,24,32,0.25)]">
              <motion.img
                data-testid="hero-image"
                src={IMAGES.hero}
                alt="Tecnico informatico al lavoro su una scheda madre"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-night/55 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/85">Laboratorio · Riparazione HW</span>
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-paper/85">
                  <Wrench size={11} /> in corso
                </span>
              </div>
            </div>
            <motion.div
              data-testid="hero-experience-badge"
              initial={{ opacity: 0, rotate: -8, scale: 0.8 }}
              animate={{ opacity: 1, rotate: -4, scale: 1 }}
              transition={{ duration: 0.7, delay: 1.3, ease: EASE }}
              className="absolute -top-5 -left-5 bg-paper border border-line rounded-xl px-4 py-3 shadow-[0_16px_40px_rgba(18,24,32,0.14)]"
            >
              <span className="font-display font-extrabold text-2xl text-copper block leading-none">20+</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-smoke">anni sul campo</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
